import { Badge, DeactivatableButton } from '@folio/components';
import { useAssertedParams } from '@folio/lib';
import { usePolicyOnDashboard } from '@folio/utils/usePolicyOnDashboard';
import { useMutation } from '@tanstack/react-query';
import {
  brokerageFirmDtoFirmTypeEnum,
  DerogationActionEnumKey,
  derogationControllerCreateDerogation,
  derogationStatusEnum,
} from 'korint-sdk';
import { MailIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useFinalizeNewBusinessRules } from '../useFinalizeNewBusinessRules';
import { useVisibilityConditionsProps } from '../useVisibilityConditionsProps';

export function DerogationButton({
  action,
  overridenBranchId,
}: {
  action: DerogationActionEnumKey;
  overridenBranchId?: string;
}) {
  const { t } = useTranslation();

  const { branchId: assertedBranchId } = useAssertedParams();
  const branchId = overridenBranchId ?? assertedBranchId;
  const { mutate: refetchPolicyOnDashboard } = usePolicyOnDashboard();

  const visibilityConditionsProps = useVisibilityConditionsProps({ action, overridenBranchId });

  const { mutate: createDerogation, isPending } = useMutation({
    mutationFn: () => derogationControllerCreateDerogation(branchId, { action }),
    onSuccess: () => refetchPolicyOnDashboard(),
  });

  const rules = useFinalizeNewBusinessRules({ overridenBranchId });

  if (!rules) {
    return null;
  }

  if (!visibilityConditionsProps) {
    return null;
  }

  const { firmType, branchStatus, derogationStatus, shouldCreateDerogation } =
    visibilityConditionsProps;

  if (firmType !== brokerageFirmDtoFirmTypeEnum.RETAIL || branchStatus === 'BRANCH_CLOSED') {
    return null;
  }

  if (derogationStatus === derogationStatusEnum.CREATED) {
    return <Badge variant="outline">{t('finalizeNewBusiness.derogation.sentToWholesaler')}</Badge>;
  }

  if (derogationStatus === derogationStatusEnum.APPROVED) {
    return (
      <Badge variant="outline">{t('finalizeNewBusiness.derogation.approvedByWholesaler')}</Badge>
    );
  }

  if (derogationStatus === derogationStatusEnum.DENIED) {
    return (
      <Badge variant="destructive">
        {t('finalizeNewBusiness.derogation.declinedByWholesaler')}
      </Badge>
    );
  }

  if (!shouldCreateDerogation) {
    return null;
  }

  return (
    <DeactivatableButton
      variant="ghost"
      className="font-bold text-blue-700"
      rules={rules}
      icon={<MailIcon className="h-4 w-4" />}
      isLoading={isPending}
      info={t('finalizeNewBusiness.derogation.info')}
      onClick={() => createDerogation()}
    >
      {t('finalizeNewBusiness.derogation.sendToWholesaler')}
    </DeactivatableButton>
  );
}
